import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext({
  productIds: [],
  has: () => false,
  toggle: () => {},
  add: () => {},
  remove: () => {},
  count: 0,
  loading: false,
});

const GUEST_KEY = "amazypet_wishlist_guest";

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [productIds, setProductIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFromBackend = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/wishlist");
      setProductIds(res.data.product_ids || []);
    } catch {
      setProductIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFromGuest = useCallback(() => {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      setProductIds(raw ? JSON.parse(raw) : []);
    } catch {
      setProductIds([]);
    }
  }, []);

  // Sync local guest wishlist to backend on login + reload
  useEffect(() => {
    const syncOnLogin = async () => {
      if (user) {
        try {
          const raw = localStorage.getItem(GUEST_KEY);
          const guest = raw ? JSON.parse(raw) : [];
          if (guest.length > 0) {
            await Promise.all(guest.map((pid) => api.post(`/wishlist/${pid}`).catch(() => {})));
            localStorage.removeItem(GUEST_KEY);
          }
        } catch {}
        loadFromBackend();
      } else {
        loadFromGuest();
      }
    };
    syncOnLogin();
  }, [user, loadFromBackend, loadFromGuest]);

  const persistGuest = (ids) => {
    localStorage.setItem(GUEST_KEY, JSON.stringify(ids));
  };

  const add = async (productId) => {
    if (productIds.includes(productId)) return;
    const next = [...productIds, productId];
    setProductIds(next);
    if (user) {
      try {
        await api.post(`/wishlist/${productId}`);
      } catch {
        // revert on failure
        setProductIds(productIds);
      }
    } else {
      persistGuest(next);
    }
  };

  const remove = async (productId) => {
    if (!productIds.includes(productId)) return;
    const next = productIds.filter((id) => id !== productId);
    setProductIds(next);
    if (user) {
      try {
        await api.delete(`/wishlist/${productId}`);
      } catch {
        setProductIds(productIds);
      }
    } else {
      persistGuest(next);
    }
  };

  const toggle = (productId) => {
    if (productIds.includes(productId)) {
      remove(productId);
    } else {
      add(productId);
    }
  };

  const has = (productId) => productIds.includes(productId);

  return (
    <WishlistContext.Provider value={{ productIds, has, toggle, add, remove, count: productIds.length, loading }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
